import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, MapPin, Star, Calendar, Trash2, Eye, EyeOff, Shield, 
  BarChart3, Search, ChevronLeft, CheckCircle, XCircle, Disc3,
  Download, RefreshCw, Globe, Clock, PoundSterling, Pencil, Tag, X,
  CheckSquare, Square, AlertTriangle, Upload, Loader2, Plus, ArrowRightLeft,
  Trophy, Music, ExternalLink, Save, Check, Ticket, Link2, Mail, CreditCard, ImageIcon, Crown, Bell, ShoppingCart, Radio, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, 
  AlertDialogTitle, AlertDialogTrigger 
} from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getAuthHeaders } from '../hooks/useAdmin';
import { AdminOverviewTab } from '../components/admin/AdminOverviewTab';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminEventsTab } from '../components/admin/AdminEventsTab';
import { AdminDJsTab } from '../components/admin/AdminDJsTab';
import { AdminWeekendsTab } from '../components/admin/AdminWeekendsTab';
import { AdminAuditTab } from '../components/admin/AdminAuditTab';
import { AdminEmailTemplatesTab } from '../components/admin/AdminEmailTemplatesTab';
import { AdminClassesTab } from '../components/admin/AdminClassesTab';
import { AdminPaymentPlansTab } from '../components/admin/AdminPaymentPlansTab';
import AdminDancesTab from '../components/admin/AdminDancesTab';
import { AdminImagesTab } from '../components/admin/AdminImagesTab';
import { AdminScrapedClassesTab } from '../components/admin/AdminScrapedClassesTab';
import { AdminSubscriptionsTab } from '../components/admin/AdminSubscriptionsTab';
import { AdminNotificationsTab } from '../components/admin/AdminNotificationsTab';
import DanceChartsManager from '../components/admin/AdminDanceChartsManager';
import BulkFetcher from '../components/admin/AdminBulkFetcher';
import { AdminScraperTab } from '../components/admin/AdminScraperTab';
import { AdminClubsTab } from '../components/admin/AdminClubsTab';
import { AdminReviewsTab } from '../components/admin/AdminReviewsTab';
import { AdminInstructorsTab } from '../components/admin/AdminInstructorsTab';
import { AdminBingoGamesTab } from '../components/admin/AdminBingoGamesTab';
import { AdminArtistsTab } from '../components/admin/AdminArtistsTab';
import { AdminShopTab } from '../components/admin/AdminShopTab';
import { AdminEmailAnalyticsTab } from '../components/admin/AdminEmailAnalyticsTab';
import { AdminMembershipTab } from '../components/admin/AdminMembershipTab';
import { AdminNewsletterTab } from '../components/admin/AdminNewsletterTab';
import { AdminDJAccessTab } from '../components/admin/AdminDJAccessTab';
import { AdminBlogTab } from '../components/admin/AdminBlogTab';
import { AdminLayout } from '../components/admin/AdminLayout';

import { API_URL } from '../utils/api';

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ clubs: 0, users: 0, reviews: 0, events: 0, djs: 0, instructors: 0 });
  const [clubs, setClubs] = useState([]);
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [djs, setDJs] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [scrapedEvents, setScrapedEvents] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ show_homepage_stats: true, show_homepage_djs: true });
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [scraperMonth, setScraperMonth] = useState('march');
  const [eventSourceFilter, setEventSourceFilter] = useState('all');
  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [eventSearchQuery, setEventSearchQuery] = useState('');
  
  // Edit scraped event state
  const [editingEvent, setEditingEvent] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    price: '',
    tag: 'Community',
    image_url: '',
    linked_club_id: '',
    ticket_url: '',
    ticket_widget_code: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [customImageUploading, setCustomImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Edit club verification state
  const [editingClubVerification, setEditingClubVerification] = useState(null);
  const [verificationData, setVerificationData] = useState({
    verified_accredited: false,
    verified_insurance: false,
    verified_ppl: false,
    verified_safeguarding: false
  });
  const [verificationUpdating, setVerificationUpdating] = useState(false);

  // Edit club slug state
  const [editingSlug, setEditingSlug] = useState(null);
  const [newSlug, setNewSlug] = useState('');
  const [slugUpdating, setSlugUpdating] = useState(false);

  // Duplicate events state
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [mergingDuplicates, setMergingDuplicates] = useState(false);

  // Legacy Review, Instructor, and Transfer dialog state removed in Phase 4C-2
  // These modals are now self-contained in AdminReviewsTab and AdminInstructorsTab

  // Available tags for scraped events
  const eventTags = ['Community', 'Featured', 'Workshop', 'Special Event', 'Festival', 'Competition', 'Social Dance'];

  // Default event images for scraped events
  const defaultEventImages = [
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/65841c5637202b42d435c0af23763090f52817addef14b1273ea06968844e5ee.png',
      label: 'Line Dancers Social'
    },
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/729d5e93e03b8090de8696797d404e495af31350509a6cdba3dafd3cf1b7f443.png',
      label: 'Dance Floor'
    },
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/1c8a12308214744b6594972f883fca53b88bc04b5561a3ca96eaa47bfd3fafc3.png',
      label: 'Live Band'
    },
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/b9127383f20c7a9f2320754282baf010a1d5e73939ff9ca2b418ce00641a0a82.png',
      label: 'Dance Class'
    },
    { 
      url: 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/a25b60625d456336cc6faf4419089c7ec45b8466868fa494c9cd9736771850e9.png',
      label: 'Cowboy Boots'
    }
  ];

  // Bulk selection state
  const [selectedClubs, setSelectedClubs] = useState(new Set());
  const [selectedDJs, setSelectedDJs] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedEvents, setSelectedEvents] = useState(new Set());
  const [selectedScrapedEvents, setSelectedScrapedEvents] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    if (user?.is_admin) {
      fetchAdminData();
    }
  }, [user]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const authHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const [statsRes, clubsRes, usersRes, reviewsRes, eventsRes, djsRes, scrapedRes, schedulerRes, instructorsRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/clubs`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/users`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/reviews`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/events`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/djs`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/scraped-events`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/scraper/status`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/instructors`, { headers: authHeaders, credentials: 'include' }),
        fetch(`${API_URL}/api/admin/site-settings`, { headers: authHeaders, credentials: 'include' })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (clubsRes.ok) setClubs(await clubsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        // API returns {events: [...], clubs: [...]} - extract just the events array
        setEvents(eventsData.events || eventsData || []);
      }
      if (djsRes.ok) setDJs(await djsRes.json());
      if (scrapedRes.ok) setScrapedEvents(await scrapedRes.json());
      if (schedulerRes.ok) setSchedulerStatus(await schedulerRes.json());
      if (instructorsRes.ok) setInstructors(await instructorsRes.json());
      if (settingsRes.ok) setSiteSettings(await settingsRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Scraper functions
  const runScheduledScrape = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraper/run-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Found ${data.new_events} new events!`);
        // Refresh scraped events
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/admin/scraped-events`, { 
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include' 
        });
        if (res.ok) setScrapedEvents(await res.json());
      } else {
        toast.error('Scheduled scrape failed');
      }
    } catch (error) {
      toast.error('Error running scheduled scrape');
    } finally {
      setScraping(false);
    }
  };

  const runScraper = async (month = null) => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const url = month 
        ? `${API_URL}/api/admin/scraper/run-month?month=${month}&year=2026`
        : `${API_URL}/api/admin/scraper/run?year=2026`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(month ? `Scraped ${data.new_saved} new events from ${month}` : 'Scraper started in background');
        // Refresh scraped events
        setTimeout(async () => {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/admin/scraped-events`, { 
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include' 
          });
          if (res.ok) setScrapedEvents(await res.json());
        }, 2000);
      } else {
        toast.error('Scraper failed');
      }
    } catch (error) {
      toast.error('Error running scraper');
    } finally {
      setScraping(false);
    }
  };

  const runBackroadBootsScraper = async () => {
    setScraping(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraper/backroad-boots`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Scraped ${data.new_saved} new events from Backroad Boots UK!`);
        // Refresh scraped events
        const res = await fetch(`${API_URL}/api/admin/scraped-events`, { 
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include' 
        });
        if (res.ok) setScrapedEvents(await res.json());
      } else {
        toast.error('Backroad Boots scraper failed');
      }
    } catch (error) {
      toast.error('Error running Backroad Boots scraper');
    } finally {
      setScraping(false);
    }
  };

  const [geocoding, setGeocoding] = useState(false);
  
  const geocodeAllEvents = async () => {
    setGeocoding(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/geocode-events`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error('Geocoding failed');
      }
    } catch (error) {
      toast.error('Error geocoding events');
    } finally {
      setGeocoding(false);
    }
  };

  const approveScrapedEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setScrapedEvents(scrapedEvents.map(e => 
          e.event_id === eventId ? { ...e, status: 'approved' } : e
        ));
        toast.success('Event approved');
      }
    } catch (error) {
      toast.error('Failed to approve event');
    }
  };

  const rejectScrapedEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setScrapedEvents(scrapedEvents.map(e => 
          e.event_id === eventId ? { ...e, status: 'rejected' } : e
        ));
        toast.success('Event rejected');
      }
    } catch (error) {
      toast.error('Failed to reject event');
    }
  };

  const deleteScrapedEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setScrapedEvents(scrapedEvents.filter(e => e.event_id !== eventId));
        toast.success('Event deleted');
      }
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Open edit modal with event data
  const openEditModal = (event) => {
    const eventDate = event.event_date ? new Date(event.event_date).toISOString().split('T')[0] : '';
    setEditFormData({
      title: event.title || '',
      description: event.description || '',
      event_date: eventDate,
      event_time: event.event_time || '',
      location: event.location || '',
      price: event.price?.toString() || '',
      tag: event.tag || 'Community',
      image_url: event.image_url || '',
      linked_club_id: event.linked_club_id || '',
      ticket_url: event.ticket_url || '',
      ticket_widget_code: event.ticket_widget_code || ''
    });
    setEditingEvent(event);
  };

  // Close edit modal
  const closeEditModal = () => {
    setEditingEvent(null);
    setEditFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '',
      location: '',
      price: '',
      tag: 'Community',
      image_url: '',
      linked_club_id: '',
      ticket_url: '',
      ticket_widget_code: ''
    });
  };

  // Update scraped event
  const updateScrapedEvent = async (e) => {
    e.preventDefault();
    if (!editingEvent) return;
    
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        title: editFormData.title,
        description: editFormData.description,
        event_date: editFormData.event_date,
        event_time: editFormData.event_time,
        location: editFormData.location || null,
        price: editFormData.price ? parseFloat(editFormData.price) : null,
        tag: editFormData.tag,
        image_url: editFormData.image_url || null,
        linked_club_id: editFormData.linked_club_id || null,
        ticket_url: editFormData.ticket_url || null,
        ticket_widget_code: editFormData.ticket_widget_code || null
      };

      const response = await fetch(`${API_URL}/api/admin/scraped-events/${editingEvent.event_id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        const updatedEvent = await response.json();
        setScrapedEvents(scrapedEvents.map(evt => 
          evt.event_id === editingEvent.event_id ? updatedEvent : evt
        ));
        toast.success('Event updated successfully');
        closeEditModal();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update event');
      }
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    } finally {
      setIsUpdating(false);
    }
  };

  // Custom image upload for scraped events
  const handleCustomImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    setCustomImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(responseText || 'Upload failed - server error');
      }

      if (!response.ok) {
        throw new Error(data.detail || data.message || 'Upload failed');
      }

      // Construct full URL for the image
      const imageUrl = `${API_URL}${data.url}`;
      setEditFormData({ ...editFormData, image_url: imageUrl });
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setCustomImageUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const approveAllPending = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/approve-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setScrapedEvents(scrapedEvents.map(e => 
          e.status === 'pending' ? { ...e, status: 'approved' } : e
        ));
        toast.success(data.message);
      }
    } catch (error) {
      toast.error('Failed to approve events');
    }
  };

  // Bulk selection helpers
  const toggleSelectAll = (items, selectedSet, setSelectedSet, idField) => {
    if (selectedSet.size === items.length) {
      setSelectedSet(new Set());
    } else {
      setSelectedSet(new Set(items.map(item => item[idField])));
    }
  };

  const toggleSelectItem = (id, selectedSet, setSelectedSet) => {
    const newSet = new Set(selectedSet);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedSet(newSet);
  };

  // Bulk delete clubs
  const bulkDeleteClubs = async () => {
    if (selectedClubs.size === 0) return;
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let deletedCount = 0;
      for (const clubId of selectedClubs) {
        const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) deletedCount++;
      }
      setClubs(clubs.filter(c => !selectedClubs.has(c.club_id)));
      setStats(prev => ({ ...prev, clubs: prev.clubs - deletedCount }));
      setSelectedClubs(new Set());
      toast.success(`Deleted ${deletedCount} clubs`);
    } catch (error) {
      toast.error('Failed to delete some clubs');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete DJs
  const bulkDeleteDJs = async () => {
    if (selectedDJs.size === 0) return;
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let deletedCount = 0;
      for (const djId of selectedDJs) {
        const response = await fetch(`${API_URL}/api/admin/djs/${djId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) deletedCount++;
      }
      setDJs(djs.filter(d => !selectedDJs.has(d.dj_id)));
      setStats(prev => ({ ...prev, djs: prev.djs - deletedCount }));
      setSelectedDJs(new Set());
      toast.success(`Deleted ${deletedCount} DJs`);
    } catch (error) {
      toast.error('Failed to delete some DJs');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk approve scraped events
  const bulkApproveScrapedEvents = async () => {
    if (selectedScrapedEvents.size === 0) return;
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let approvedCount = 0;
      for (const eventId of selectedScrapedEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/approve`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) approvedCount++;
      }
      setScrapedEvents(scrapedEvents.map(e => 
        selectedScrapedEvents.has(e.event_id) ? { ...e, status: 'approved' } : e
      ));
      setSelectedScrapedEvents(new Set());
      toast.success(`Approved ${approvedCount} events`);
    } catch (error) {
      toast.error('Failed to approve some events');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk reject scraped events
  const bulkRejectScrapedEvents = async () => {
    if (selectedScrapedEvents.size === 0) return;
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let rejectedCount = 0;
      for (const eventId of selectedScrapedEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}/reject`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) rejectedCount++;
      }
      setScrapedEvents(scrapedEvents.map(e => 
        selectedScrapedEvents.has(e.event_id) ? { ...e, status: 'rejected' } : e
      ));
      setSelectedScrapedEvents(new Set());
      toast.success(`Rejected ${rejectedCount} events`);
    } catch (error) {
      toast.error('Failed to reject some events');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk delete scraped events
  const bulkDeleteScrapedEvents = async () => {
    if (selectedScrapedEvents.size === 0) return;
    setBulkActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      let deletedCount = 0;
      for (const eventId of selectedScrapedEvents) {
        const response = await fetch(`${API_URL}/api/admin/scraped-events/${eventId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (response.ok) deletedCount++;
      }
      setScrapedEvents(scrapedEvents.filter(e => !selectedScrapedEvents.has(e.event_id)));
      setSelectedScrapedEvents(new Set());
      toast.success(`Deleted ${deletedCount} events`);
    } catch (error) {
      toast.error('Failed to delete some events');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Delete all past events
  const [deletingPastEvents, setDeletingPastEvents] = useState(false);
  
  const deletePastEvents = async () => {
    setDeletingPastEvents(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/clear-past`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh the events list
        const res = await fetch(`${API_URL}/api/admin/scraped-events`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (res.ok) {
          setScrapedEvents(await res.json());
        }
      } else {
        toast.error('Failed to delete past events');
      }
    } catch (error) {
      toast.error('Error deleting past events');
    } finally {
      setDeletingPastEvents(false);
    }
  };

  // Find duplicate events
  const findDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/duplicates`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setDuplicateGroups(data.duplicate_groups);
        setShowDuplicates(true);
        if (data.duplicate_groups.length === 0) {
          toast.info('No duplicate events found');
        } else {
          toast.success(`Found ${data.duplicate_groups.length} duplicate groups`);
        }
      } else {
        toast.error('Failed to find duplicates');
      }
    } catch (error) {
      toast.error('Error finding duplicates');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  // Auto-link events to clubs
  const [autoLinking, setAutoLinking] = useState(false);
  const [linkSuggestions, setLinkSuggestions] = useState([]);
  const [showLinkSuggestions, setShowLinkSuggestions] = useState(false);
  const [confirmingLink, setConfirmingLink] = useState(null);
  
  const autoLinkEvents = async () => {
    setAutoLinking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/auto-link?preview=true`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.suggestions && data.suggestions.length > 0) {
          setLinkSuggestions(data.suggestions);
          setShowLinkSuggestions(true);
          toast.success(`Found ${data.suggestions.length} potential matches to review`);
        } else {
          toast.info('No potential matches found');
        }
      } else {
        toast.error('Failed to find matches');
      }
    } catch (error) {
      toast.error('Error finding matches');
    } finally {
      setAutoLinking(false);
    }
  };

  const confirmLink = async (eventId, clubId) => {
    setConfirmingLink(eventId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/admin/scraped-events/confirm-link?event_id=${eventId}&club_id=${clubId}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Remove from suggestions
        setLinkSuggestions(prev => prev.filter(s => s.event_id !== eventId));
        // Refresh events
        const res = await fetch(`${API_URL}/api/admin/scraped-events`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (res.ok) {
          setScrapedEvents(await res.json());
        }
      } else {
        toast.error('Failed to link event');
      }
    } catch (error) {
      toast.error('Error linking event');
    } finally {
      setConfirmingLink(null);
    }
  };

  const rejectSuggestion = (eventId) => {
    setLinkSuggestions(prev => prev.filter(s => s.event_id !== eventId));
    toast.info('Suggestion dismissed');
  };

  // Merge duplicate events
  const mergeDuplicates = async (primaryId, duplicateIds) => {
    setMergingDuplicates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/scraped-events/merge?primary_event_id=${primaryId}&${duplicateIds.map(id => `duplicate_event_ids=${id}`).join('&')}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh events and duplicates
        const res = await fetch(`${API_URL}/api/admin/scraped-events`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include'
        });
        if (res.ok) {
          setScrapedEvents(await res.json());
        }
        // Re-check for duplicates
        findDuplicates();
      } else {
        toast.error('Failed to merge events');
      }
    } catch (error) {
      toast.error('Error merging events');
    } finally {
      setMergingDuplicates(false);
    }
  };

  const handleDeleteClub = async (clubId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (response.ok) {
        setClubs(clubs.filter(c => c.club_id !== clubId));
        setStats(prev => ({ ...prev, clubs: prev.clubs - 1 }));
        toast.success('Club deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete club');
      }
    } catch (error) {
      console.error('Delete club error:', error);
      toast.error('Failed to delete club');
    }
  };

  const handleToggleFeatured = async (clubId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ is_featured: !currentValue })
      });
      if (response.ok) {
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, is_featured: !currentValue } : c
        ));
        toast.success(currentValue ? 'Club unfeatured' : 'Club featured');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update club');
      }
    } catch (error) {
      console.error('Toggle featured error:', error);
      toast.error('Failed to update club');
    }
  };

  // Visibility toggle handlers
  const handleToggleClubVisibility = async (clubId, isCurrentlyHidden) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}/toggle-visibility`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, is_hidden: data.is_hidden } : c
        ));
        toast.success(data.is_hidden ? 'Club hidden from public' : 'Club is now visible');
      }
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleToggleDJVisibility = async (djId, isCurrentlyHidden) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/djs/${djId}/toggle-visibility`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDJs(djs.map(d => 
          d.dj_id === djId ? { ...d, is_hidden: data.is_hidden } : d
        ));
        toast.success(data.is_hidden ? 'DJ hidden from public' : 'DJ is now visible');
      }
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleToggleInstructorVisibility = async (instructorId, isCurrentlyHidden) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/instructors/${instructorId}/toggle-visibility`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInstructors(instructors.map(i => 
          i.instructor_id === instructorId ? { ...i, is_hidden: data.is_hidden } : i
        ));
        toast.success(data.is_hidden ? 'Instructor hidden from public' : 'Instructor is now visible');
      }
    } catch (error) {
      toast.error('Failed to toggle visibility');
    }
  };

  const handleToggleClubDJLive = async (clubId, isCurrentlyEnabled) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}/toggle-dj-live`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, dj_live_enabled: data.dj_live_enabled } : c
        ));
        toast.success(data.dj_live_enabled ? 'DJ Live enabled for club' : 'DJ Live disabled for club');
      }
    } catch (error) {
      toast.error('Failed to toggle DJ Live');
    }
  };

  const handleToggleClubBingo = async (clubId, isCurrentlyEnabled) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}/toggle-bingo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, bingo_enabled: data.bingo_enabled } : c
        ));
        toast.success(data.bingo_enabled ? 'Bingo enabled for club' : 'Bingo disabled for club');
      }
    } catch (error) {
      toast.error('Failed to toggle Bingo');
    }
  };

  const handleTogglePlatformFeeExempt = async (clubId) => {
    if (!clubId) {
      console.error('Club ID is undefined or null');
      toast.error('Club ID is missing. Please refresh and try again.');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Not authenticated. Please log in again.');
      return;
    }
    
    console.log('Toggling platform fee exempt for club:', clubId);
    
    const url = `${API_URL}/api/admin/clubs/${encodeURIComponent(clubId)}/toggle-platform-fee-exempt`;
    console.log('Toggle URL:', url);
    
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            console.log('XHR completed with status:', xhr.status);
            try {
              const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
              resolve({
                ok: xhr.status >= 200 && xhr.status < 300,
                status: xhr.status,
                data
              });
            } catch (e) {
              console.error('Parse error:', e);
              resolve({
                ok: false,
                status: xhr.status,
                data: { detail: 'Failed to parse response' }
              });
            }
          }
        };
        
        xhr.onerror = function(e) {
          console.error('XHR onerror triggered:', e);
          reject(new Error('Network error - request failed'));
        };
        
        xhr.ontimeout = function() {
          reject(new Error('Request timeout'));
        };
        
        xhr.timeout = 30000;
        xhr.send(null);
      });
      
      if (result.ok) {
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, platform_fee_exempt: result.data.platform_fee_exempt } : c
        ));
        toast.success(result.data.platform_fee_exempt ? 'Platform fees waived for club' : 'Platform fees enabled for club');
      } else {
        console.error('Toggle error response:', result.data);
        toast.error(result.data?.detail || `Failed to toggle platform fee exemption (${result.status})`);
      }
    } catch (error) {
      console.error('Toggle fee exemption error:', error);
      toast.error(`Network error: ${error.message}`);
    }
  };

  const handleToggleClubTicketing = async (clubId) => {
    if (!clubId) {
      console.error('Club ID is undefined or null');
      toast.error('Club ID is missing. Please refresh and try again.');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Not authenticated. Please log in again.');
      return;
    }
    
    console.log('Toggling ticketing for club:', clubId);
    
    const url = `${API_URL}/api/admin/clubs/${encodeURIComponent(clubId)}/toggle-ticketing`;
    console.log('Toggle URL:', url);
    
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            console.log('XHR completed with status:', xhr.status);
            console.log('XHR response:', xhr.responseText);
            try {
              const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
              resolve({
                ok: xhr.status >= 200 && xhr.status < 300,
                status: xhr.status,
                data
              });
            } catch (e) {
              console.error('Parse error:', e);
              resolve({
                ok: false,
                status: xhr.status,
                data: { detail: 'Failed to parse response' }
              });
            }
          }
        };
        
        xhr.onerror = function(e) {
          console.error('XHR onerror triggered:', e);
          reject(new Error('Network error - request failed'));
        };
        
        xhr.ontimeout = function() {
          console.error('XHR timeout');
          reject(new Error('Request timeout'));
        };
        
        xhr.timeout = 30000;
        xhr.send(null);
      });
      
      console.log('Result:', result);
      if (result.ok) {
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, ticketing_enabled: result.data.ticketing_enabled } : c
        ));
        toast.success(result.data.ticketing_enabled ? 'Ticketing enabled for club' : 'Ticketing disabled for club');
      } else {
        console.error('Toggle error response:', result.data);
        toast.error(result.data?.detail || `Failed to toggle ticketing (${result.status})`);
      }
    } catch (error) {
      console.error('Toggle ticketing error:', error);
      toast.error(`Network error: ${error.message}`);
    }
  };

  const handleTogglePartner = async (clubId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Not authenticated');
      return;
    }
    
    const url = `${API_URL}/api/admin/clubs/${encodeURIComponent(clubId)}/toggle-partner`;
    
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        
        xhr.onreadystatechange = function() {
          if (xhr.readyState === 4) {
            try {
              const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
              resolve({
                ok: xhr.status >= 200 && xhr.status < 300,
                status: xhr.status,
                data
              });
            } catch (e) {
              resolve({
                ok: false,
                status: xhr.status,
                data: { detail: 'Failed to parse response' }
              });
            }
          }
        };
        
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.timeout = 30000;
        xhr.send(null);
      });
      
      if (result.ok) {
        setClubs(clubs.map(c => 
          c.club_id === clubId ? { ...c, is_partner: result.data.is_partner } : c
        ));
        toast.success(result.data.is_partner ? 'Partner status enabled' : 'Partner status disabled');
      } else {
        toast.error(result.data?.detail || 'Failed to toggle partner status');
      }
    } catch (error) {
      toast.error(`Network error: ${error.message}`);
    }
  };


  // Legacy Transfer modal handlers removed in Phase 4C-2
  // Instructor transfer is now in AdminInstructorsTab
  // NOTE: Club transfer needs to be added to AdminClubsTab as a separate task

  // Club verification functions
  const openVerificationModal = (club) => {
    setEditingClubVerification(club);
    setVerificationData({
      verified_accredited: club.verified_accredited || false,
      verified_insurance: club.verified_insurance || false,
      verified_ppl: club.verified_ppl || false,
      verified_safeguarding: club.verified_safeguarding || false
    });
  };

  const closeVerificationModal = () => {
    setEditingClubVerification(null);
    setVerificationData({
      verified_accredited: false,
      verified_insurance: false,
      verified_ppl: false,
      verified_safeguarding: false
    });
  };

  const updateClubVerifications = async () => {
    if (!editingClubVerification) return;
    setVerificationUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${editingClubVerification.club_id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(verificationData)
      });
      if (response.ok) {
        setClubs(clubs.map(c => 
          c.club_id === editingClubVerification.club_id 
            ? { ...c, ...verificationData } 
            : c
        ));
        toast.success('Verifications updated');
        closeVerificationModal();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to update verifications');
      }
    } catch (error) {
      toast.error('Failed to update verifications');
    } finally {
      setVerificationUpdating(false);
    }
  };

  // Slug editing functions
  const startEditingSlug = (club) => {
    setEditingSlug(club.club_id);
    setNewSlug(club.slug || '');
  };

  const cancelEditingSlug = () => {
    setEditingSlug(null);
    setNewSlug('');
  };

  const updateClubSlug = async (clubId) => {
    if (!newSlug.trim()) {
      toast.error('Slug cannot be empty');
      return;
    }
    
    setSlugUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/clubs/${clubId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ slug: newSlug.toLowerCase().trim() })
      });
      
      if (response.ok) {
        setClubs(clubs.map(c => 
          c.club_id === clubId 
            ? { ...c, slug: newSlug.toLowerCase().trim() } 
            : c
        ));
        toast.success('Slug updated successfully');
        setEditingSlug(null);
        setNewSlug('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update slug');
      }
    } catch (error) {
      toast.error('Failed to update slug');
    } finally {
      setSlugUpdating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setUsers(users.filter(u => u.user_id !== userId));
        setStats(prev => ({ ...prev, users: prev.users - 1 }));
        toast.success('User deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleImpersonateUser = async (targetUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/impersonate/${targetUser.user_id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Store original admin token for returning
        localStorage.setItem('admin_token', token);
        // Set the impersonated user's token
        localStorage.setItem('token', data.token);
        toast.success(`Now viewing as ${data.user.name || data.user.email}. Go to Dashboard to see their view.`);
        // Open dashboard in new tab so admin doesn't lose their place
        window.open('/dashboard', '_blank');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to impersonate user');
      }
    } catch (error) {
      console.error('Impersonate user error:', error);
      toast.error('Failed to impersonate user');
    }
  };

  const handleToggleBookingManager = async (userId, makeBookingManager) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/booking-manager`, {
        method: 'PATCH',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_booking_manager: makeBookingManager }),
        credentials: 'include'
      });
      if (response.ok) {
        // Update local state
        setUsers(users.map(u => 
          u.user_id === userId 
            ? { ...u, is_booking_manager: makeBookingManager, role: makeBookingManager ? 'booking_manager' : 'user' } 
            : u
        ));
        toast.success(makeBookingManager ? 'User is now a Booking Manager' : 'Booking Manager role removed');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Toggle booking manager error:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setReviews(reviews.filter(r => r.review_id !== reviewId));
        setStats(prev => ({ ...prev, reviews: prev.reviews - 1 }));
        toast.success('Review deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete review');
      }
    } catch (error) {
      console.error('Delete review error:', error);
      toast.error('Failed to delete review');
    }
  };


  // Legacy Review modal handlers removed in Phase 4C-2
  // Review management is now self-contained in AdminReviewsTab

  const handleDeleteEvent = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setEvents(prev => Array.isArray(prev) ? prev.filter(e => e.event_id !== eventId) : []);
        setStats(prev => ({ ...prev, events: Math.max(0, (prev.events || 0) - 1) }));
        toast.success('Event deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete event');
      }
    } catch (error) {
      console.error('Delete event error:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleDeleteDJ = async (djId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/djs/${djId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setDJs(djs.filter(d => d.dj_id !== djId));
        setStats(prev => ({ ...prev, djs: prev.djs - 1 }));
        toast.success('DJ deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete DJ');
      }
    } catch (error) {
      console.error('Delete DJ error:', error);
      toast.error('Failed to delete DJ');
    }
  };

  const handleToggleDJFeatured = async (djId, currentValue) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/djs/${djId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({ is_featured: !currentValue })
      });
      if (response.ok) {
        setDJs(djs.map(d => 
          d.dj_id === djId ? { ...d, is_featured: !currentValue } : d
        ));
        toast.success(currentValue ? 'DJ unfeatured' : 'DJ featured');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update DJ');
      }
    } catch (error) {
      console.error('Toggle DJ featured error:', error);
      toast.error('Failed to update DJ');
    }
  };

  // Toggle DJ Live access for a DJ
  const handleToggleDJLive = async (djId, currentValue) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/djs/${djId}/dj-live`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Update local state
        setDJs(djs.map(dj => 
          dj.dj_id === djId ? { ...dj, dj_live_enabled: data.dj_live_enabled } : dj
        ));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to toggle DJ Live');
      }
    } catch (error) {
      console.error('Toggle DJ Live error:', error);
      toast.error('Failed to toggle DJ Live');
    }
  };


  // Legacy Instructor modal handlers removed in Phase 4C-2
  // Instructor management is now self-contained in AdminInstructorsTab

  const handleDeleteInstructor = async (instructorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/instructors/${instructorId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setInstructors(instructors.filter(i => i.instructor_id !== instructorId));
        setStats(prev => ({ ...prev, instructors: prev.instructors - 1 }));
        toast.success('Instructor deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete instructor');
      }
    } catch (error) {
      console.error('Delete instructor error:', error);
      toast.error('Failed to delete instructor');
    }
  };

  // Site settings toggle
  const toggleHomepageStats = async () => {
    const newValue = !siteSettings.show_homepage_stats;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/site-settings`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...siteSettings, show_homepage_stats: newValue })
      });
      if (response.ok) {
        setSiteSettings({ ...siteSettings, show_homepage_stats: newValue });
        toast.success(newValue ? 'Homepage stats enabled' : 'Homepage stats disabled');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const toggleHomepageDJs = async () => {
    const newValue = !siteSettings.show_homepage_djs;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/site-settings`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...siteSettings, show_homepage_djs: newValue })
      });
      if (response.ok) {
        setSiteSettings({ ...siteSettings, show_homepage_djs: newValue });
        toast.success(newValue ? 'Homepage DJs section enabled' : 'Homepage DJs section disabled');
      }
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  // Check access - admin or booking_manager
  const isAdmin = user?.is_admin;
  const isBookingManager = user?.role === 'booking_manager';
  
  if (!isAdmin && !isBookingManager) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-700 mb-2">Access Denied</h1>
          <p className="text-slate-500 mb-6">You don't have admin privileges.</p>
          <Button onClick={() => navigate('/')} className="bg-violet-600 hover:bg-violet-500">
            Go Home
          </Button>
        </div>
      </div>
    );
  }
  
  // For booking managers, force the weekends tab and restrict access
  const effectiveTab = isBookingManager && !isAdmin ? 'weekends' : activeTab;

  const filteredClubs = clubs.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star key={i} size={12} className={i < Math.floor(rating) ? 'fill-violet-500 text-violet-500' : 'text-slate-300'} />
    ));
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  // For non-admin users (DJ/club access), show old interface
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-100" data-testid="admin-page">
        {/* Header for non-admin users */}
        <div className="bg-slate-900 text-white">
          <div className="container mx-auto px-6 lg:px-12 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  className="text-white hover:bg-slate-800"
                  onClick={() => navigate('/dashboard')}
                  data-testid="back-to-dashboard-btn"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Dashboard
                </Button>
                <div className="flex items-center gap-2">
                  <Shield className="w-6 h-6 text-violet-500" />
                  <h1 className="text-xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Admin Panel
                  </h1>
                </div>
              </div>
              <Badge className="bg-violet-500 text-white">
                {user?.email}
              </Badge>
            </div>
          </div>
        </div>

        {/* Main Content for non-admin */}
        <div className="container mx-auto px-6 lg:px-12 py-8">
          <Tabs value={effectiveTab} onValueChange={undefined}>
            <div className="flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-violet-600" />
              <h2 className="text-lg font-semibold text-slate-900">Booking Management</h2>
            </div>

            <TabsContent value="weekends">
              <AdminWeekendsTab 
                searchQuery={searchQuery}
                schedulerStatus={schedulerStatus}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    );
  }

  // Admin interface with new sidebar layout
  return (
    <AdminLayout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      user={user}
      onLogout={handleLogout}
    >
      <div data-testid="admin-page">
        {/* Stats Cards - Only show on Dashboard/Overview */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Total Clubs', value: stats.clubs, icon: MapPin, color: 'bg-violet-500' },
              { label: 'Total DJs', value: stats.djs, icon: Disc3, color: 'bg-violet-500' },
              { label: 'Total Users', value: stats.users, icon: Users, color: 'bg-teal-500' },
              { label: 'Total Reviews', value: stats.reviews, icon: Star, color: 'bg-purple-500' },
              { label: 'Total Events', value: stats.events, icon: Calendar, color: 'bg-blue-500' }
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Search bar for relevant tabs */}
        {activeTab !== 'overview' && (
          <div className="mb-6">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white"
                data-testid="admin-search-input"
              />
            </div>
          </div>
        )}

        {/* Tab Content - Using hidden Tabs component for state management */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Hidden TabsList - navigation is handled by sidebar */}
          <TabsList className="hidden">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="clubs">Clubs</TabsTrigger>
            <TabsTrigger value="classes">Classes</TabsTrigger>
            <TabsTrigger value="instructors">Instructors</TabsTrigger>
            <TabsTrigger value="djs">DJs</TabsTrigger>
            <TabsTrigger value="artists">Artists</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
            <TabsTrigger value="weekends">Weekends</TabsTrigger>
            <TabsTrigger value="payment-plans">Payment Plans</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="dance-charts">Dance Charts</TabsTrigger>
            <TabsTrigger value="bingo-dances">Bingo Dances</TabsTrigger>
            <TabsTrigger value="bingo-games">Bingo Games</TabsTrigger>
            <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
            <TabsTrigger value="blog">Blog</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="images">Images</TabsTrigger>
            <TabsTrigger value="shop">Shop</TabsTrigger>
            <TabsTrigger value="scraper">Scraper</TabsTrigger>
            <TabsTrigger value="scraped-classes">Scraped Classes</TabsTrigger>
            <TabsTrigger value="dj-access">DJ Access</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="email-templates">Email Templates</TabsTrigger>
            <TabsTrigger value="email-analytics">Email Analytics</TabsTrigger>
            <TabsTrigger value="audit">Audit</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <AdminOverviewTab 
              clubs={clubs}
              users={users}
              siteSettings={siteSettings}
              toggleHomepageStats={toggleHomepageStats}
              toggleHomepageDJs={toggleHomepageDJs}
            />
          </TabsContent>

          {/* Clubs Tab */}
          <TabsContent value="clubs">
            <AdminClubsTab searchQuery={searchQuery} onDataChange={fetchAdminData} />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <AdminUsersTab 
              users={users}
              searchQuery={searchQuery}
              handleDeleteUser={handleDeleteUser}
              handleImpersonateUser={handleImpersonateUser}
              handleToggleBookingManager={handleToggleBookingManager}
              refreshUsers={fetchAdminData}
            />
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <AdminReviewsTab onDataChange={fetchAdminData} />
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <AdminEventsTab 
              events={events}
              handleDeleteEvent={handleDeleteEvent}
            />
          </TabsContent>

          {/* Instructors Tab */}
          <TabsContent value="instructors">
            <AdminInstructorsTab onDataChange={fetchAdminData} />
          </TabsContent>


          {/* DJs Tab - Admin only */}
          
          <TabsContent value="djs">
            <AdminDJsTab
              djs={djs}
              handleToggleDJFeatured={handleToggleDJFeatured}
              handleToggleDJVisibility={handleToggleDJVisibility}
              handleToggleDJLive={handleToggleDJLive}
              handleDeleteDJ={handleDeleteDJ}
              onViewDJ={(dj) => navigate(`/djs/${dj.dj_id}`)}
            />
          </TabsContent>


          {/* DJ Access Tab - Admin only */}
          
          <TabsContent value="dj-access">
            <AdminDJAccessTab />
          </TabsContent>


          {/* Artists Tab - Admin only */}
          
          <TabsContent value="artists">
            <AdminArtistsTab />
          </TabsContent>



          {/* Event Scraper Tab - Admin only */}
          
          <TabsContent value="scraper">
            <AdminScraperTab clubs={clubs} onDataChange={fetchAdminData} />
          </TabsContent>


          {/* Dance Charts Tab - Admin only */}
          
          <TabsContent value="dance-charts">
            <DanceChartsManager />
          </TabsContent>


          {/* Weekends Tab - Available to both admin and booking managers */}
          <TabsContent value="weekends">
            <AdminWeekendsTab searchQuery={searchQuery} isBookingManager={isBookingManager && !isAdmin} />
          </TabsContent>
          
          {/* Audit Log Tab - Admin only */}
          
          <TabsContent value="audit">
            <AdminAuditTab />
          </TabsContent>

          
          {/* Images Tab - Admin only */}
          
          <TabsContent value="images">
            <AdminImagesTab />
          </TabsContent>

          
          {/* Scraped Classes Tab - Admin only */}
          
          <TabsContent value="scraped-classes">
            <AdminScrapedClassesTab />
          </TabsContent>

          
          {/* Email Templates Tab - Admin only */}
          
          <TabsContent value="email-templates">
            <AdminEmailTemplatesTab />
          </TabsContent>


          {/* Email Analytics Tab - Admin only */}
          
          <TabsContent value="email-analytics">
            <AdminEmailAnalyticsTab />
          </TabsContent>

          
          {/* Classes Tab - Admin only */}
          
          <TabsContent value="classes">
            <AdminClassesTab />
          </TabsContent>


          {/* Payment Plans Tab - Admin only */}
          
          <TabsContent value="payment-plans">
            <AdminPaymentPlansTab />
          </TabsContent>


          {/* Shop Tab - Admin only */}
          
          <TabsContent value="shop">
            <AdminShopTab />
          </TabsContent>


          {/* Members Tab - Admin only */}
          
          <TabsContent value="members">
            <AdminMembershipTab />
          </TabsContent>


          {/* Newsletter Tab - Admin only */}
          
          <TabsContent value="newsletter">
            <AdminNewsletterTab />
          </TabsContent>


          {/* Blog Tab - Admin only */}
          
          <TabsContent value="blog">
            <AdminBlogTab />
          </TabsContent>

          
          {/* Bingo Dances Tab - Admin only */}
          
          <TabsContent value="bingo-dances">
            <AdminDancesTab />
          </TabsContent>


          {/* Bingo Games Tab - Admin only */}
          
          <TabsContent value="bingo-games">
            <AdminBingoGamesTab />
          </TabsContent>


          {/* Subscriptions Tab - Admin only */}
          
          <TabsContent value="subscriptions">
            <AdminSubscriptionsTab />
          </TabsContent>


          {/* Push Notifications Tab - Admin only */}
          <TabsContent value="notifications">
            <AdminNotificationsTab token={localStorage.getItem('token')} />
          </TabsContent>
        </Tabs>

        {/* Edit Scraped Event Modal */}
      {editingEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">Edit Event</h2>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={closeEditModal}
                data-testid="close-edit-modal"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <form onSubmit={updateScrapedEvent} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <Input
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  required
                  placeholder="Event title"
                  data-testid="edit-event-title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description *</label>
                <textarea
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                  placeholder="Event description"
                  data-testid="edit-event-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                  <Input
                    type="date"
                    value={editFormData.event_date}
                    onChange={(e) => setEditFormData({ ...editFormData, event_date: e.target.value })}
                    required
                    data-testid="edit-event-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time *</label>
                  <Input
                    value={editFormData.event_time}
                    onChange={(e) => setEditFormData({ ...editFormData, event_time: e.target.value })}
                    required
                    placeholder="e.g., 7:30pm - 11:00pm"
                    data-testid="edit-event-time"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                <Input
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                  placeholder="Event location"
                  data-testid="edit-event-location"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Price (£)</label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editFormData.price}
                    onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                    placeholder="0.00"
                    data-testid="edit-event-price"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tag</label>
                  <Select 
                    value={editFormData.tag} 
                    onValueChange={(value) => setEditFormData({ ...editFormData, tag: value })}
                  >
                    <SelectTrigger data-testid="edit-event-tag">
                      <SelectValue placeholder="Select tag" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTags.map(tag => (
                        <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Link to Club */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Link to Club (Optional)</label>
                <Select 
                  value={editFormData.linked_club_id || "none"} 
                  onValueChange={(value) => setEditFormData({ ...editFormData, linked_club_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="edit-event-linked-club">
                    <SelectValue placeholder="Select a club to link" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No club linked</SelectItem>
                    {clubs.map(club => (
                      <SelectItem key={club.club_id} value={club.club_id}>{club.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Linked events will appear on the club's page</p>
              </div>
              
              {/* Event Image Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Event Image</label>
                
                {/* Preset Images */}
                <p className="text-xs text-slate-500 mb-2">Choose from presets:</p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {defaultEventImages.map((img, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setEditFormData({ ...editFormData, image_url: img.url })}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        editFormData.image_url === img.url 
                          ? 'border-violet-500 ring-2 ring-violet-500/30' 
                          : 'border-slate-200 hover:border-violet-300'
                      }`}
                      data-testid={`select-image-${index}`}
                    >
                      <img 
                        src={img.url} 
                        alt={img.label}
                        className="w-full h-16 object-cover"
                      />
                      {editFormData.image_url === img.url && (
                        <div className="absolute inset-0 bg-violet-500/20 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-violet-600" />
                        </div>
                      )}
                      <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs py-0.5 px-1 truncate">
                        {img.label}
                      </span>
                    </button>
                  ))}
                </div>
                
                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-2 bg-white text-slate-500">Or upload custom image</span>
                  </div>
                </div>
                
                {/* Custom Image Upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleCustomImageUpload}
                  className="hidden"
                  data-testid="custom-image-upload-input"
                />
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={customImageUploading}
                  className={`w-full border-2 border-dashed rounded-lg p-4 text-center transition-all ${
                    customImageUploading
                      ? 'border-slate-300 bg-slate-50 cursor-not-allowed'
                      : 'border-slate-300 hover:border-violet-400 hover:bg-slate-50 cursor-pointer'
                  }`}
                  data-testid="custom-image-upload-btn"
                >
                  {customImageUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                      <span className="text-sm text-slate-600">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-6 h-6 text-slate-500" />
                      <span className="text-sm text-slate-600">Click to upload custom image</span>
                      <span className="text-xs text-slate-500">JPG, PNG or WebP (max 5MB)</span>
                    </div>
                  )}
                </button>
                
                {/* Current Image Preview */}
                {editFormData.image_url && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <img 
                        src={editFormData.image_url} 
                        alt="Selected" 
                        className="w-16 h-12 object-cover rounded"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Image</text></svg>';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-700">Selected Image</p>
                        <p className="text-xs text-slate-500 truncate">
                          {editFormData.image_url.includes('/api/uploads/') 
                            ? 'Custom uploaded image' 
                            : 'Preset image'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditFormData({ ...editFormData, image_url: '' })}
                        className="text-red-500 hover:text-red-600 h-8 px-2"
                        data-testid="clear-image-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Ticket Section */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-green-500" />
                  Ticket Sales (Optional)
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ticket URL</label>
                    <Input
                      value={editFormData.ticket_url || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, ticket_url: e.target.value })}
                      placeholder="https://ticketscandy.com/your-event"
                      data-testid="edit-event-ticket-url"
                    />
                    <p className="text-xs text-slate-500 mt-1">Link to external ticket purchase page</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Ticket Widget Code</label>
                    <textarea
                      value={editFormData.ticket_widget_code || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, ticket_widget_code: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder:text-slate-500 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none font-mono text-xs"
                      placeholder='<iframe src="https://embed.ticketscandy.com/..." ...></iframe>'
                      data-testid="edit-event-ticket-widget"
                    />
                    <p className="text-xs text-slate-500 mt-1">Paste TicketsCandy or other widget embed code (iframe)</p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={closeEditModal}
                  data-testid="cancel-edit-event"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isUpdating}
                  className="bg-violet-600 hover:bg-violet-500"
                  data-testid="save-edit-event"
                >
                  {isUpdating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : 'Save Changes'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Club Verification Modal */}
      {editingClubVerification && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Verification Badges</h2>
                <p className="text-sm text-slate-500 mt-1">{editingClubVerification.name}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={closeVerificationModal}
                data-testid="close-verification-modal"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600 mb-4">
                Select the verifications this club has completed. These badges will be displayed on the club's profile.
              </p>
              
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <Checkbox
                  checked={verificationData.verified_accredited}
                  onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_accredited: checked }))}
                  className="mt-0.5"
                  data-testid="verify-accredited"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-slate-900">Accredited Instructor</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Instructor has recognized accreditation/qualifications</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <Checkbox
                  checked={verificationData.verified_insurance}
                  onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_insurance: checked }))}
                  className="mt-0.5"
                  data-testid="verify-insurance"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-slate-900">Insurance</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Club has valid public liability insurance</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <Checkbox
                  checked={verificationData.verified_ppl}
                  onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_ppl: checked }))}
                  className="mt-0.5"
                  data-testid="verify-ppl"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Disc3 className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-slate-900">PPL License</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Club has PPL/PRS music license</p>
                </div>
              </label>
              
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors">
                <Checkbox
                  checked={verificationData.verified_safeguarding}
                  onCheckedChange={(checked) => setVerificationData(prev => ({ ...prev, verified_safeguarding: checked }))}
                  className="mt-0.5"
                  data-testid="verify-safeguarding"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-slate-900">Under 18s Safeguarding</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">DBS checks and safeguarding policies in place</p>
                </div>
              </label>
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={closeVerificationModal}
                data-testid="cancel-verification"
              >
                Cancel
              </Button>
              <Button 
                onClick={updateClubVerifications}
                disabled={verificationUpdating}
                className="bg-violet-600 hover:bg-violet-500"
                data-testid="save-verification"
              >
                {verificationUpdating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : 'Save Verifications'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Legacy Review, Instructor, and Transfer modals removed in Phase 4C-2 */}
      {/* These are now self-contained in AdminReviewsTab and AdminInstructorsTab */}

      </div>
    </AdminLayout>
  );
}
